import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, ref, onMounted, watch, nextTick, type Ref } from "vue";
import { delay } from "@/utils";

interface Props {
    position?: 'top' | 'bottom' | 'left' | 'right' | 'top-left';
    color?: 'black' | 'blue' | 'grey';
    borderColor?: 'blue';
    minWidth?: string;
    width?: string;
    maxWidth?: string;
    isClickToShow?: boolean;
    offset?: number;
    isDisabled?: boolean;
    isDesignText?: boolean;
    isArrow?: boolean;
    isOneLine?: boolean;
    isTeleportToBody?: boolean;
    isWithByActivator?: boolean;
    customClass?: string;
    tooltipId?: string | number;
    closeOnScroll?: HTMLElement;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BaseTooltip',
  props: {
    position: { default: 'top' },
    color: { default: 'black' },
    borderColor: null,
    minWidth: null,
    width: null,
    maxWidth: null,
    isClickToShow: { type: Boolean },
    offset: { default: 6 },
    isDisabled: { type: Boolean },
    isDesignText: { type: Boolean },
    isArrow: { type: Boolean, default: true },
    isOneLine: { type: Boolean },
    isTeleportToBody: { type: Boolean },
    isWithByActivator: { type: Boolean },
    customClass: null,
    tooltipId: null,
    closeOnScroll: null
  },
  emits: ['enter-mouse'],
  setup(__props: any, { expose, emit: emits }) {

const props = __props as { position: 'top' | 'bottom' | 'left' | 'right' | 'top-left', color: 'black' | 'blue' | 'grey', borderColor?: 'blue', minWidth?: string, width?: string, maxWidth?: string, isClickToShow?: boolean, offset: number, isDisabled?: boolean, isDesignText?: boolean, isArrow: boolean, isOneLine?: boolean, isTeleportToBody?: boolean, isWithByActivator?: boolean, customClass?: string, tooltipId?: string | number, closeOnScroll?: HTMLElement }





/** Задержка перед закрытием при mouseleave — время перевести курсор на тултип */
const TOOLTIP_LEAVE_GRACE_MS = 300;

const positionStyle = ref('');
const isShowTooltip = ref(false);
const isPositionReady = ref(false); // для телепорта: скрываем тултип до расчёта позиции
const tooltipEl: Ref<HTMLElement | null> = ref(null);
const activatorEl: Ref<HTMLElement | null> = ref(null);

// Таймеры и интервалы для очистки
let positionCheckInterval: ReturnType<typeof setInterval> | null = null;
let hoverCheckTimeout: ReturnType<typeof setTimeout> | null = null;
let scrollHandler: (() => void) | null = null;
let isTooltipInBody = false;

// Отслеживание изменений видимости tooltip для позиционирования
watch(isShowTooltip, async (newValue) => {
    if (newValue) {
        positionStyle.value = '';
        isPositionReady.value = !props.isTeleportToBody;
        await nextTick();
        if (props.isTeleportToBody) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setPosition();
                    isPositionReady.value = true;
                });
            });
        } else {
            setPosition();
        }
        startPositionCheck();
    } else {
        positionStyle.value = '';
        isPositionReady.value = false;
        stopPositionCheck();
    }
});

// Очистка при размонтировании
onBeforeUnmount(() => {
    cleanup();
});

onMounted(() => {
    if (props.closeOnScroll) {
        scrollHandler = () => {
            isShowTooltip.value = false;
        };
        props.closeOnScroll.addEventListener('scroll', scrollHandler);
    }
});

const cleanup = () => {
    stopPositionCheck();

    if (hoverCheckTimeout) {
        clearTimeout(hoverCheckTimeout);
        hoverCheckTimeout = null;
    }

    if (scrollHandler && props.closeOnScroll) {
        props.closeOnScroll.removeEventListener('scroll', scrollHandler);
        scrollHandler = null;
    }

    // Удаляем tooltip из body, если он был добавлен туда
    if (props.isTeleportToBody && tooltipEl.value && isTooltipInBody) {
        const tooltip = tooltipEl.value;
        if (tooltip.parentNode === document.body) {
            document.body.removeChild(tooltip);
        }
        isTooltipInBody = false;
    }
};

const setPosition = async() => {
    if (!isShowTooltip.value) return;

    const tooltip = tooltipEl.value;
    const container = activatorEl.value;
    const activator = container?.firstElementChild as HTMLElement | null;

    if (!tooltip || !activator || !container) {
        positionStyle.value = '';
        isShowTooltip.value = false;
        return;
    }
    const rect = activator.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const relLeft = rect.left - containerRect.left;
    const relTop = rect.top - containerRect.top;
    if (props.isTeleportToBody) {
        if (!isTooltipInBody || tooltip.parentNode !== document.body) {
            document.body.appendChild(tooltip);
            isTooltipInBody = true;
        }
        await nextTick();
        const anchorRect = activator.getBoundingClientRect();
        const width = tooltip.clientWidth;
        const height = tooltip.clientHeight;
        const viewportWidth = document.documentElement.clientWidth;
        const viewportHeight = document.documentElement.clientHeight;

        switch (props.position) {
        case 'top': {
            let y = anchorRect.top - height - props.offset;
            let x = anchorRect.left + (anchorRect.width - width) / 2;
            y = Math.max(0, Math.min(y, viewportHeight - height));
            x = Math.max(0, Math.min(x, viewportWidth - width));
            positionStyle.value = `top: ${y}px; left: ${x}px;`;
            break;
        }
        case 'bottom': {
            let y = anchorRect.bottom + props.offset;
            let x = anchorRect.left + (anchorRect.width - width) / 2;
            y = Math.max(0, Math.min(y, viewportHeight - height));
            x = Math.max(0, Math.min(x, viewportWidth - width));
            positionStyle.value = `top: ${y}px; left: ${x}px;`;
            break;
        }
        case 'left': {
            let y = anchorRect.top + (anchorRect.height - height) / 2;
            let x = anchorRect.left - props.offset - width;
            y = Math.max(0, Math.min(y, viewportHeight - height));
            x = Math.max(0, Math.min(x, viewportWidth - width));
            positionStyle.value = `left: ${x}px; top: ${y}px;`;
            break;
        }
        case 'right': {
            let y = anchorRect.top + (anchorRect.height - height) / 2;
            let x = anchorRect.right + props.offset;
            y = Math.max(0, Math.min(y, viewportHeight - height));
            x = Math.max(0, Math.min(x, viewportWidth - width));
            positionStyle.value = `left: ${x}px; top: ${y}px;`;
            break;
        }
        case 'top-left': {
            let y = anchorRect.top - height - props.offset;
            let x = anchorRect.right - width;
            y = Math.max(0, Math.min(y, viewportHeight - height));
            x = Math.max(0, Math.min(x, viewportWidth - width));
            positionStyle.value = `top: ${y}px; left: ${x}px;`;
            tooltip.style.setProperty('--right', `${anchorRect.width / 2 - 3}px`);
            tooltip.style.setProperty('--bottom', '-6px');
            tooltip.style.setProperty('--left', 'unset');
            break;
        }
        }
        return;
    }

    const width = tooltip.clientWidth;
    const height = tooltip.clientHeight;

    // Позиционирование относительно контейнера (position: absolute внутри .main-tooltip__activator)
    if (props.position === 'top') {
        positionStyle.value = `top: ${relTop - height - props.offset}px; left: ${relLeft + (rect.width - width) / 2}px;`;
    } else if (props.position === 'bottom') {
        positionStyle.value = `top: ${relTop + rect.height + props.offset}px; left: ${relLeft + (rect.width - width) / 2}px;`;
    } else if (props.position === 'top-left') {
        positionStyle.value = `top: ${relTop - height - props.offset}px; left: ${relLeft + rect.width - width}px;`;
        tooltip.style.setProperty('--right', `${rect.width / 2 - 3}px`);
        tooltip.style.setProperty('--bottom', '-6px');
        tooltip.style.setProperty('--left', 'unset');
    } else if (props.position === 'left') {
        positionStyle.value = `left: ${relLeft - width - props.offset}px; top: ${relTop + (rect.height - height) / 2}px;`;
    } else {
        // right
        positionStyle.value = `left: ${relLeft + rect.width + props.offset}px; top: ${relTop + (rect.height - height) / 2}px;`;
    }
};

const checkHoverState = (): boolean => {
    const activator = activatorEl.value?.firstElementChild as HTMLElement | null;
    const tooltip = tooltipEl.value;

    if (!activator || !tooltip) return false;

    return activator.matches(':hover') || tooltip.matches(':hover');
};

const startPositionCheck = () => {
    stopPositionCheck();

    // Запускаем проверку позиции через 1 секунду после показа
    hoverCheckTimeout = setTimeout(() => {
        if (!isShowTooltip.value) return;

        // Используем setInterval вместо рекурсии для контроля
        positionCheckInterval = setInterval(() => {
            if (!isShowTooltip.value) {
                stopPositionCheck();
                return;
            }

            const isHovered = checkHoverState();
            if (!isHovered) {
                isShowTooltip.value = false;
                stopPositionCheck();
            }
        }, 1000);
    }, 1000);
};

const stopPositionCheck = () => {
    if (positionCheckInterval) {
        clearInterval(positionCheckInterval);
        positionCheckInterval = null;
    }

    if (hoverCheckTimeout) {
        clearTimeout(hoverCheckTimeout);
        hoverCheckTimeout = null;
    }
};

const getStyle = computed(() => {
    if (!isShowTooltip.value || props.isDisabled) {
        return `visibility: hidden; ${positionStyle.value}`;
    }
    const visible = props.isTeleportToBody ? isPositionReady.value : true;
    const styles: string[] = [
        positionStyle.value,
        props.isTeleportToBody ? 'position: fixed' : '',
        `visibility: ${visible ? 'visible' : 'hidden'}`,
    ].filter(Boolean);

    if (props.minWidth) {
        const minWidthValue = isNaN(Number(props.minWidth)) ? props.minWidth : `${props.minWidth}px`;
        styles.push(`min-width: ${minWidthValue}`);
    }

    if (props.maxWidth) {
        const maxWidthValue = isNaN(Number(props.maxWidth)) ? props.maxWidth : `${props.maxWidth}px`;
        styles.push(`max-width: ${maxWidthValue}`);
    }

    if (props.isWithByActivator) {
        const activator = activatorEl.value?.firstElementChild as HTMLElement | null;
        if (activator) {
            styles.push(`width: ${activator.clientWidth}px`);
        }
    } else if (props.width) {
        const widthValue = isNaN(Number(props.width)) ? props.width : `${props.width}px`;
        styles.push(`width: ${widthValue}`);
    }

    return styles.join('; ');
});

const getClasses = computed(() => {
    const classes: string[] = [];

    if (props.isArrow) classes.push('--is-arrow');
    if (props.isDesignText) classes.push('--is-design-text');
    if (props.isOneLine) classes.push('--is-one-line');
    if (props.borderColor) classes.push(`--border-${props.borderColor}`);
    classes.push(`--${props.color}`);
    classes.push(`--${props.position}`);

    return classes.join(' ');
});

const enterOnActivator = () => {
    if (props.isClickToShow) return;
    isShowTooltip.value = true;
    emits('enter-mouse');
};

const leaveOnActivator = async () => {
    if (props.isClickToShow || !isShowTooltip.value) return;

    await delay(TOOLTIP_LEAVE_GRACE_MS);

    if (!checkHoverState()) {
        isShowTooltip.value = false;
    }
};

const enterOnTooltip = () => {
    if (props.isClickToShow) return;
    // При наведении на tooltip оставляем его открытым
    isShowTooltip.value = true;
};

const leaveOnTooltip = async () => {
    if (props.isClickToShow) return;

    // Задержка, чтобы успеть перейти с tooltip на activator
    await delay(TOOLTIP_LEAVE_GRACE_MS);

    // Проверяем, не находится ли курсор на activator или tooltip
    if (!checkHoverState()) {
        isShowTooltip.value = false;
    }
};

const openTooltip = () => {
    if (!props.isClickToShow) return;
    isShowTooltip.value = true;
};

const closeTooltip = () => {
    if (!props.isClickToShow) return;
    isShowTooltip.value = false;
};

expose({
    closeTooltip,
    isShowTooltip,
});

return { __sfc: true,props, emits, TOOLTIP_LEAVE_GRACE_MS, positionStyle, isShowTooltip, isPositionReady, tooltipEl, activatorEl, positionCheckInterval, hoverCheckTimeout, scrollHandler, isTooltipInBody, cleanup, setPosition, checkHoverState, startPositionCheck, stopPositionCheck, getStyle, getClasses, enterOnActivator, leaveOnActivator, enterOnTooltip, leaveOnTooltip, openTooltip, closeTooltip }
}

})